import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import Hero from "../components/Hero";
import BackgroundVideo from "../components/BackgroundVideo";
import PropertyCard from "../components/PropertyCard";
import ServicesSection from "../components/ServicesSection";
import RentalsLive from "../components/RentalsLive";
import { SOCIALS } from "../components/socials";
import useReveal from "../hooks/useReveal";
import { SERVICES, STATS, TESTIMONIALS } from "../mock/mock";
import { listProperties } from "../lib/api";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [total, setTotal] = useState(0);
  useReveal([featured]);

  useEffect(() => {
    listProperties().then((data) => { setFeatured(data.slice(0, 9)); setTotal(data.length); }).catch(() => {});
  }, []);

  return (
    <div>
      <BackgroundVideo />
      <Hero />

      {/* Intro / mission */}
      <section className="py-24 glass-light">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="gold-sep mx-auto mb-8 reveal" />
          <h2 className="reveal font-serif-display text-3xl md:text-4xl text-[#0b1f33] leading-snug">
            Peu importe le projet de vos r&ecirc;ves, nous avons les outils et la
            solution.
          </h2>
          <p className="reveal reveal-delay-1 text-slate-500 mt-6 text-lg leading-relaxed">
            Bella Edifices accompagne les investisseurs francophones dans
            l&apos;acquisition et le financement de biens immobiliers au Mexique &mdash;
            de la premi&egrave;re visite &agrave; la remise des cl&eacute;s.
          </p>
        </div>
      </section>

      {/* Stats band */}
      <section className="glass-dark py-16">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-10 text-center">
          {STATS.map((s, i) => (
            <div key={s.label} className={`reveal reveal-delay-${i % 3}`}>
              <p className="font-serif-display text-4xl md:text-5xl text-[#2f74c9]">
                {s.value}
              </p>
              <p className="text-white/70 text-sm mt-2 tracking-wide">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Services */}
      <section className="py-24 glass-light">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="reveal text-[#2f74c9] tracking-[0.3em] text-xs uppercase mb-3">
              Services cl&eacute; en main
            </p>
            <h2 className="reveal font-serif-display text-3xl md:text-4xl text-[#0b1f33]">
              Tout ce dont vous avez besoin, au m&ecirc;me endroit
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {SERVICES.map((srv, i) => {
              const Icon = Icons[srv.icon] || Icons.Circle;
              return (
                <div
                  key={srv.label}
                  className={`reveal reveal-delay-${i % 3} group bg-white rounded-lg p-7 border border-slate-100 hover:border-[#2f74c9] transition-colors`}
                >
                  <div className="w-12 h-12 rounded-full bg-[#0b1f33] group-hover:bg-[#2f74c9] flex items-center justify-center transition-colors">
                    <Icon size={22} className="text-[#2f74c9] group-hover:text-[#0b1f33] transition-colors" />
                  </div>
                  <p className="mt-5 font-600 text-[#0b1f33] leading-snug">{srv.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured properties */}
      <section className="py-24 glass-light border-t border-white/40">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-4">
            <div>
              <p className="reveal text-[#1156a7] tracking-[0.3em] text-xs uppercase mb-3">
                Nos offres
              </p>
              <h2 className="reveal font-serif-display text-3xl md:text-4xl text-[#0b1f33]">
                Propri&eacute;t&eacute;s en vedette
              </h2>
              <p className="reveal text-slate-500 mt-2 text-sm">
                <span className="font-700 text-[#1156a7]">{total}</span> propri&eacute;t&eacute;s disponibles au Mexique
              </p>
            </div>
            <Link
              to="/proprietes"
              className="reveal btn-gold px-6 py-3 rounded-sm font-600 text-sm"
            >
              Voir toutes les propri&eacute;t&eacute;s
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {featured.map((p, i) => (
              <div key={p.id} className={`reveal reveal-delay-${i % 3}`}>
                <PropertyCard property={p} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Locations & Gestion (rentals + property management) */}
      <ServicesSection />
      <RentalsLive />

      {/* Testimonials */}
      <section className="py-24 glass-dark">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="gold-sep mx-auto mb-6 reveal" />
            <h2 className="reveal font-serif-display text-3xl md:text-4xl text-white">
              Ils nous ont fait confiance
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {TESTIMONIALS.map((t, i) => (
              <div
                key={t.id}
                className={`reveal reveal-delay-${i % 3} bg-white/5 border border-white/10 rounded-lg p-8`}
              >
                <Icons.Quote size={30} className="text-[#2f74c9] mb-4" />
                <p className="text-white/85 leading-relaxed italic">&ldquo;{t.text}&rdquo;</p>
                <div className="mt-6 pt-6 border-t border-white/10">
                  <p className="text-white font-600">{t.name}</p>
                  <p className="text-white/50 text-sm">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Suivez-nous */}
      <section className="py-16 glass-light border-t border-white/40">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-[#1156a7] tracking-[0.3em] text-xs uppercase mb-3">Réseaux sociaux</p>
          <h2 className="font-serif-display text-3xl text-[#0b1f33]">Suivez-nous</h2>
          <p className="text-slate-500 mt-2">Actualités, nouvelles propriétés et coulisses de nos projets au Mexique.</p>
          <div className="flex justify-center gap-4 mt-7">
            {SOCIALS.map(({ name, href, Icon }) => (
              <a
                key={name}
                href={href}
                target="_blank"
                rel="noreferrer"
                aria-label={name}
                className="w-12 h-12 rounded-full border border-slate-200 flex items-center justify-center text-[#0b1f33] hover:bg-[#1156a7] hover:text-white hover:border-[#1156a7] transition-colors"
              >
                <Icon size={20} />
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 glass-dark">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="reveal font-serif-display text-3xl md:text-5xl text-white leading-snug">
            Pr&ecirc;t &agrave; investir au Mexique&nbsp;?
          </h2>
          <p className="reveal reveal-delay-1 text-white/75 mt-5 text-lg">
            Planifions ensemble votre projet lors d&apos;un appel gratuit et sans
            engagement.
          </p>
          <Link
            to="/contact"
            className="reveal reveal-delay-2 btn-gold inline-block mt-8 px-10 py-4 rounded-sm font-600 tracking-wide"
          >
            Prendre rendez-vous
          </Link>
        </div>
      </section>
    </div>
  );
}
